/**
 * Owner-first value cards: the numbers a small-business owner checks every
 * week, in their words. Read from the same YOM reports (and token) as the
 * charts below them.
 */
import { useQuery } from '@tanstack/react-query';
import {
  ArrowDownRight,
  ArrowUpRight,
  MessageCircle,
  MessagesSquare,
  Phone,
  ShoppingBag,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { qk } from '@/lib/queryKeys';
import { cn } from '@/lib/utils';
import type { EmbedTokenManager } from '@/lib/yom/tokenManager';
import { fetchYomReport, type BreakdownData, type OverviewData } from '@/lib/yom/reportClient';
import { previousRange, daysInRange, type DateRange } from '@/lib/yom/range';
import {
  buildNumbers,
  change,
  type ValueCardKey,
  type ValueNumbers,
  type ValueSnapshot,
} from '@/lib/yom/valueCards';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  projectId: string;
  manager: EmbedTokenManager;
  range: DateRange;
  compare: boolean;
  cards: ValueCardKey[];
  currency: string;
  onSnapshot?: (s: ValueSnapshot) => void;
}

async function loadSnapshot(
  manager: EmbedTokenManager,
  range: DateRange,
  compare: boolean,
  signal?: AbortSignal
): Promise<ValueSnapshot> {
  const bundle = await manager.getBundle();
  const src = bundle.sources.site;
  const getToken = (rejected?: string | null) => manager.getToken('site', { rejected });
  const span = { from: range.from, to: range.to };
  const prev = previousRange(range);

  const [overview, events, channels, prevEvents, prevChannels] = await Promise.all([
    fetchYomReport<OverviewData>(
      src,
      'overview',
      { ...span, compare: compare ? null : 'false' },
      getToken,
      signal
    ),
    fetchYomReport<BreakdownData>(
      src,
      'breakdown',
      { ...span, dim: 'event', limit: 100 },
      getToken,
      signal
    ),
    fetchYomReport<BreakdownData>(
      src,
      'breakdown',
      { ...span, dim: 'd:channel', event: 'contact_click', limit: 10 },
      getToken,
      signal
    ).catch(() => null),
    compare
      ? fetchYomReport<BreakdownData>(
          src,
          'breakdown',
          { ...prev, dim: 'event', limit: 100 },
          getToken,
          signal
        )
      : Promise.resolve(null),
    compare
      ? fetchYomReport<BreakdownData>(
          src,
          'breakdown',
          { ...prev, dim: 'd:channel', event: 'contact_click', limit: 10 },
          getToken,
          signal
        ).catch(() => null)
      : Promise.resolve(null),
  ]);
  return {
    current: buildNumbers(overview.kpis, events, channels),
    previous:
      compare && overview.previous
        ? buildNumbers(overview.previous, prevEvents, prevChannels)
        : null,
  };
}

export function useValueSnapshot(
  projectId: string,
  manager: EmbedTokenManager | null,
  range: DateRange,
  compare: boolean
) {
  return useQuery({
    queryKey: qk.analyticsValues(projectId, range.from, range.to, compare),
    queryFn: ({ signal }) => loadSnapshot(manager!, range, compare, signal),
    enabled: Boolean(manager),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

interface CardSpec {
  label: string;
  icon: LucideIcon;
  value: (v: ValueNumbers, fmt: Formatters) => string;
  detail: (v: ValueNumbers, fmt: Formatters) => string | null;
  /** Number the change is computed on. */
  basis: (v: ValueNumbers) => number;
  tone: string;
}

interface Formatters {
  count: (n: number) => string;
  money: (n: number) => string;
}

const CARD_SPECS: Record<ValueCardKey, CardSpec> = {
  visitors: {
    label: 'Visitors',
    icon: Users,
    value: (v, f) => f.count(v.visitors),
    detail: (v, f) => (v.pageviews ? `${f.count(v.pageviews)} pages viewed` : null),
    basis: (v) => v.visitors,
    tone: 'bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300',
  },
  leads: {
    label: 'Leads',
    icon: MessageCircle,
    value: (v, f) => f.count(v.leads),
    detail: () => 'From your forms and chat',
    basis: (v) => v.leads,
    tone: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300',
  },
  contacts: {
    label: 'Calls & WhatsApp',
    icon: Phone,
    value: (v, f) => f.count(v.contacts),
    detail: (v, f) =>
      v.calls || v.whatsapp
        ? `${f.count(v.calls)} ${v.calls === 1 ? 'call' : 'calls'} · ${f.count(v.whatsapp)} WhatsApp`
        : 'Taps on your phone, WhatsApp and email links',
    basis: (v) => v.contacts,
    tone: 'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300',
  },
  orders: {
    label: 'Orders & revenue',
    icon: ShoppingBag,
    value: (v, f) => f.money(v.revenue),
    detail: (v, f) => `${f.count(v.orders)} ${v.orders === 1 ? 'order' : 'orders'}`,
    basis: (v) => v.revenue,
    tone: 'bg-violet-50 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300',
  },
  chats: {
    label: 'Chatbot chats',
    icon: MessagesSquare,
    value: (v, f) => f.count(v.chats),
    detail: () => 'Chats with your assistant',
    basis: (v) => v.chats,
    tone: 'bg-sky-50 text-sky-600 dark:bg-sky-500/15 dark:text-sky-300',
  },
};

export function ValueCards({ projectId, manager, range, compare, cards, currency }: Props) {
  const q = useValueSnapshot(projectId, manager, range, compare);
  const fmt: Formatters = {
    count: (n) =>
      new Intl.NumberFormat(undefined, { notation: n >= 10_000 ? 'compact' : 'standard' }).format(
        n
      ),
    money: (n) => {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: Number.isInteger(n) || n >= 100 ? 0 : 2,
        }).format(n);
      } catch {
        return n.toLocaleString();
      }
    },
  };
  const days = daysInRange(range);
  const periodWord = days === 1 ? 'yesterday' : `previous ${days} days`;
  const odd = cards.length % 2 === 1;

  if (q.isError && !q.data) {
    return (
      <div className="bg-card flex min-h-[148px] flex-col items-center justify-center gap-3 rounded-xl border p-6 text-center shadow sm:flex-row sm:text-left">
        <AlertCircle className="text-muted-foreground h-5 w-5 shrink-0" aria-hidden />
        <p className="text-muted-foreground flex-1 text-sm">
          We couldn&apos;t load your numbers right now.
        </p>
        <Button variant="outline" size="sm" onClick={() => q.refetch()}>
          <RefreshCw className="h-4 w-4" />
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'grid grid-cols-2 gap-3 sm:gap-4',
        cards.length >= 5
          ? 'lg:grid-cols-5'
          : cards.length === 4
            ? 'lg:grid-cols-4'
            : 'lg:grid-cols-3'
      )}
    >
      {cards.map((key, i) => {
        const spec = CARD_SPECS[key];
        const Icon = spec.icon;
        const cur = q.data?.current;
        const prev = q.data?.previous;
        const delta =
          cur && compare ? change(spec.basis(cur), prev ? spec.basis(prev) : null) : null;
        return (
          <div
            key={key}
            className={cn(
              'bg-card flex min-h-[148px] flex-col rounded-xl border p-4 shadow sm:p-5',
              odd && i === 0 && 'col-span-2 lg:col-span-1'
            )}
          >
            <div className="flex items-center gap-2">
              <span className={cn('rounded-md p-1.5', spec.tone)}>
                <Icon className="h-4 w-4" aria-hidden />
              </span>
              <span className="text-muted-foreground text-sm font-medium leading-tight">
                {spec.label}
              </span>
            </div>
            {cur ? (
              <>
                <div className="mt-3 truncate text-2xl font-semibold tabular-nums tracking-tight sm:text-3xl">
                  {spec.value(cur, fmt)}
                </div>
                <div className="text-muted-foreground mt-0.5 line-clamp-2 text-xs leading-snug">
                  {spec.detail(cur, fmt) ?? '\u00a0'}
                </div>
                <div className="mt-auto pt-2 text-xs leading-snug">
                  {delta !== null && Math.abs(delta) < 0.005 ? (
                    <span className="text-muted-foreground">
                      No change<span className="hidden sm:inline"> vs {periodWord}</span>
                    </span>
                  ) : delta !== null ? (
                    <span
                      className={cn(
                        'inline-flex items-center gap-0.5 font-medium',
                        delta >= 0
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-red-600 dark:text-red-400'
                      )}
                    >
                      {delta >= 0 ? (
                        <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
                      ) : (
                        <ArrowDownRight className="h-3.5 w-3.5" aria-hidden />
                      )}
                      {Math.abs(Math.round(delta * 100))}%
                      <span className="text-muted-foreground hidden font-normal sm:inline">
                        &nbsp;vs {periodWord}
                      </span>
                      <span className="sr-only sm:hidden"> vs {periodWord}</span>
                    </span>
                  ) : compare ? (
                    <span className="text-muted-foreground">Nothing to compare yet</span>
                  ) : null}
                </div>
              </>
            ) : (
              <>
                <div className="bg-muted mt-3 h-8 w-20 animate-pulse rounded" />
                <div className="bg-muted mt-1.5 h-3 w-28 animate-pulse rounded" />
                <div className="bg-muted mt-auto h-3 w-24 animate-pulse rounded" />
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
